import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Trash2, Loader2, Zap, CheckCircle, IndianRupee, Settings,
  ChevronDown, Check, History, FileSpreadsheet, Droplets, Wallet, CreditCard, Receipt, FileText, Save, Users, Download, Search, RefreshCw, FileDown, X, RotateCcw, Edit, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

/* ── Multi-select units dropdown ── */
function UnitMultiSelect({ allUnits, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const isAll = selected.length === 0 || selected.length === allUnits.length;
  const toggle = (uid) => selected.includes(uid) ? onChange(selected.filter(id => id !== uid)) : onChange([...selected, uid]);
  const selectAll = () => onChange([]);
  const label = isAll ? 'All Units' : selected.length === 1 ? allUnits.find(u => u.unit_id === selected[0])?.unit_number || '1 unit' : `${selected.length} units`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 text-xs justify-between min-w-[120px] font-normal">{label} <ChevronDown size={12} className="ml-1 text-slate-400" /></Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 max-h-64 overflow-y-auto" align="start">
        <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-slate-50 text-left" onClick={selectAll}>
          <div className={`w-4 h-4 rounded border flex items-center justify-center ${isAll ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>{isAll && <Check size={10} className="text-white" />}</div>
          <span className="font-medium">Apply to All</span>
        </button>
        <Separator className="my-1" />
        {allUnits.map(u => {
          const checked = isAll || selected.includes(u.unit_id);
          return (
            <button key={u.unit_id} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-slate-50 text-left" onClick={() => isAll ? onChange(allUnits.map(x => x.unit_id).filter(id => id !== u.unit_id)) : toggle(u.unit_id)}>
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>{checked && <Check size={10} className="text-white" />}</div>
              <span>{u.unit_number}</span>
              <span className="text-slate-400 ml-auto">{u.resident_name || 'Vacant'}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

/* ── Payment Modal (with partial payment) ── */
function PaymentModal({ open, onClose, unit, payments, onSave, loading }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [transactionId, setTransactionId] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  const totalPending = unit?.pending_amount || 0;

  useEffect(() => {
    if (open) setAmount(totalPending.toString());
  }, [open, totalPending]);

  const handleSave = () => {
    const payAmount = parseFloat(amount) || 0;
    if (payAmount <= 0) { toast.error('Enter valid amount'); return; }
    // Allow overpayment - excess will be added as wallet credit
    onSave({ amount: payAmount, payment_method: method, transaction_id: transactionId, transaction_date: transactionDate, notes });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Outfit']"><Receipt size={18} className="text-emerald-500" />Record Payment - {unit?.unit_number}</DialogTitle>
          <DialogDescription>Enter payment details. Overpayments are credited to wallet.</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="bg-slate-50 p-3 rounded-lg flex justify-between items-center">
            <div><p className="text-xs text-slate-500">Total Pending</p><p className="text-xl font-bold text-amber-600">₹{totalPending.toLocaleString()}</p></div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Amount to Pay</Label>
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Enter amount" className="h-10 text-lg font-semibold" />
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setAmount(totalPending.toString())}>Full Amount</Button>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setAmount(Math.round(totalPending / 2).toString())}>50%</Button>
            </div>
            {parseFloat(amount) > totalPending && totalPending > 0 && (
              <p className="text-xs text-emerald-600 mt-2">💳 Excess ₹{(parseFloat(amount) - totalPending).toLocaleString()} will be added as wallet credit</p>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Payment Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="razorpay">Razorpay Online</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Transaction ID</Label>
              <Input value={transactionId} onChange={e => setTransactionId(e.target.value)} placeholder="Reference #" className="h-10" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Date</Label>
              <Input type="date" value={transactionDate} onChange={e => setTransactionDate(e.target.value)} className="h-10" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..." rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
            {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : null}Pay ₹{parseFloat(amount || 0).toLocaleString()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Statement Modal ── */
function StatementModal({ open, onClose, unit }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && unit) {
      setLoading(true);
      api.get(`/admin/payment-statement/${unit.unit_id}`).then(res => {
        setEntries(res.data.entries || []);
        setSummary(res.data.summary || {});
      }).catch(() => toast.error('Failed to load statement')).finally(() => setLoading(false));
    }
  }, [open, unit]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Outfit']"><FileText size={18} className="text-blue-500" />Payment Statement - {unit?.unit_number}</DialogTitle>
          <DialogDescription>{unit?.resident_name || 'Vacant'}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto py-4">
          {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div> : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-slate-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Total Charges</p><p className="text-lg font-bold">₹{summary.total_charges?.toLocaleString() || 0}</p></div>
                <div className="bg-emerald-50 p-3 rounded-lg"><p className="text-xs text-emerald-600">Total Paid</p><p className="text-lg font-bold text-emerald-700">₹{summary.total_paid?.toLocaleString() || 0}</p></div>
                <div className="bg-amber-50 p-3 rounded-lg"><p className="text-xs text-amber-600">Balance Due</p><p className="text-lg font-bold text-amber-700">₹{summary.current_balance?.toLocaleString() || 0}</p></div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Description</TableHead>
                    <TableHead className="text-xs text-right">Debit</TableHead>
                    <TableHead className="text-xs text-right">Credit</TableHead>
                    <TableHead className="text-xs text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e, i) => (
                    <TableRow key={i} className={e.type === 'credit' ? 'bg-emerald-50/30' : ''}>
                      <TableCell className="text-xs">{e.date?.split('T')[0] || '-'}</TableCell>
                      <TableCell className="text-sm">{e.description}{e.reference && <span className="text-xs text-slate-400 ml-2">#{e.reference}</span>}</TableCell>
                      <TableCell className="text-sm text-right text-red-600">{e.debit > 0 ? `₹${e.debit.toLocaleString()}` : '-'}</TableCell>
                      <TableCell className="text-sm text-right text-emerald-600">{e.credit > 0 ? `₹${e.credit.toLocaleString()}` : '-'}</TableCell>
                      <TableCell className="text-sm text-right font-medium">₹{e.balance?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Transactions Modal (Edit/Delete Payments) ── */
function TransactionsModal({ open, onClose, unit, monthLabel, onUpdate }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (open && unit && monthLabel) {
      setLoading(true);
      api.get(`/admin/transactions/by-unit-month?unit_id=${unit.unit_id}&month_label=${encodeURIComponent(monthLabel)}`)
        .then(res => setTransactions(res.data || []))
        .catch(() => toast.error('Failed to load transactions'))
        .finally(() => setLoading(false));
    }
  }, [open, unit, monthLabel]);

  const handleDelete = async (txnId) => {
    if (!window.confirm('Delete this payment record? This will reduce the paid amount.')) return;
    setActionLoading(true);
    try {
      await api.delete(`/admin/transactions/${txnId}`);
      setTransactions(prev => prev.filter(t => t.transaction_id !== txnId));
      toast.success('Payment deleted');
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (txn) => {
    setEditingTxn(txn);
    setEditAmount(txn.amount.toString());
  };

  const handleSaveEdit = async () => {
    if (!editingTxn) return;
    const newAmount = parseFloat(editAmount);
    if (isNaN(newAmount) || newAmount < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setActionLoading(true);
    try {
      await api.put(`/admin/transactions/${editingTxn.transaction_id}`, { amount: newAmount });
      setTransactions(prev => prev.map(t => 
        t.transaction_id === editingTxn.transaction_id ? { ...t, amount: newAmount } : t
      ));
      setEditingTxn(null);
      toast.success('Payment updated');
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setActionLoading(false);
    }
  };

  const totalPaid = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Outfit']">
            <CreditCard size={18} className="text-emerald-500" />
            Payment Records - {unit?.unit_number}
          </DialogTitle>
          <DialogDescription>{monthLabel} • {unit?.resident_name || 'Vacant'}</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>
          ) : transactions.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No payments recorded for this period</p>
          ) : (
            <>
              <div className="bg-emerald-50 p-3 rounded-lg mb-4 flex justify-between items-center">
                <span className="text-sm text-emerald-700">Total Paid</span>
                <span className="text-xl font-bold text-emerald-700">₹{totalPaid.toLocaleString()}</span>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {transactions.map(txn => (
                  <div key={txn.transaction_id} className="border rounded-lg p-3 flex items-center justify-between hover:bg-slate-50">
                    {editingTxn?.transaction_id === txn.transaction_id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          type="number"
                          value={editAmount}
                          onChange={e => setEditAmount(e.target.value)}
                          className="w-32 h-8"
                          autoFocus
                        />
                        <Button size="sm" className="h-8 bg-emerald-600" onClick={handleSaveEdit} disabled={actionLoading}>
                          {actionLoading ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingTxn(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-emerald-700">₹{txn.amount?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">
                            {txn.payment_method} • {txn.transaction_date?.split('T')[0] || txn.created_at?.split('T')[0]}
                            {txn.reference_id && <span className="ml-2">Ref: {txn.reference_id}</span>}
                          </p>
                          {txn.notes && <p className="text-xs text-slate-400 mt-1">{txn.notes}</p>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleEdit(txn)} disabled={actionLoading}>
                            <Edit size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(txn.transaction_id)} disabled={actionLoading}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Meter Reading Type Modal ── */
function ServiceModal({ open, onClose, onSave, allUnits }) {
  const [name, setName] = useState('');
  const [unitLabel, setUnitLabel] = useState('units');
  const [startMonth, setStartMonth] = useState(new Date().getMonth() + 1);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [showInitialReadings, setShowInitialReadings] = useState(false);
  const [newServiceId, setNewServiceId] = useState(null);
  const [initialReadings, setInitialReadings] = useState({});

  useEffect(() => {
    if (open) {
      setName('');
      setUnitLabel('units');
      setStartMonth(new Date().getMonth() + 1);
      setStartYear(new Date().getFullYear());
      setShowInitialReadings(false);
      setNewServiceId(null);
      setInitialReadings({});
    }
  }, [open]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Enter a name'); return; }
    setLoading(true);
    try {
      const res = await api.post('/admin/consumption-services', { 
        name: name.trim(), 
        unit_label: unitLabel.trim() || 'units',
        start_month: startMonth,
        start_year: startYear
      });
      toast.success(`${name} meter type added`);
      setNewServiceId(res.data.service_id);
      setShowInitialReadings(true);
    } catch { toast.error('Failed to add meter type'); }
    finally { setLoading(false); }
  };

  const handleSaveInitialReadings = async () => {
    if (!newServiceId) return;
    setLoading(true);
    try {
      const readings = Object.entries(initialReadings)
        .filter(([_, val]) => val !== '' && val !== undefined)
        .map(([unitId, val]) => ({ unit_id: unitId, opening_reading: parseFloat(val) || 0 }));
      
      if (readings.length > 0) {
        await api.post(`/admin/consumption-services/${newServiceId}/initial-readings`, { readings });
        toast.success('Initial readings saved');
      }
      onSave();
      onClose();
    } catch { toast.error('Failed to save initial readings'); }
    finally { setLoading(false); }
  };

  const handleSkipInitialReadings = () => {
    onSave();
    onClose();
  };

  if (showInitialReadings) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Outfit']"><Droplets size={18} className="text-blue-500" />Set Opening Readings - {name}</DialogTitle>
            <DialogDescription>Enter initial meter readings for each unit (optional). These will be used as starting values.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Unit</TableHead>
                  <TableHead className="text-xs">Resident</TableHead>
                  <TableHead className="text-xs text-right">Opening Reading ({unitLabel})</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allUnits.map(u => (
                  <TableRow key={u.unit_id}>
                    <TableCell className="text-sm font-medium">{u.unit_number}</TableCell>
                    <TableCell className="text-xs text-slate-500">{u.resident_name || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Input 
                        type="number" 
                        min="0" 
                        value={initialReadings[u.unit_id] || ''} 
                        onChange={e => setInitialReadings(prev => ({ ...prev, [u.unit_id]: e.target.value }))} 
                        className="w-32 h-8 text-sm text-right" 
                        placeholder="0" 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleSkipInitialReadings}>Skip</Button>
            <Button onClick={handleSaveInitialReadings} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              {loading && <Loader2 size={14} className="animate-spin mr-2" />}Save & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Outfit']"><Droplets size={18} className="text-blue-500" />Add Meter Reading Type</DialogTitle>
          <DialogDescription>Define a custom meter reading type for your community</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Name *</Label>
            <Input 
              value={name} 
              onChange={e => setName(e.target.value)} 
              placeholder="e.g., Water, Electricity, Gas, DG Backup" 
              className="h-10"
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Unit of Measurement</Label>
            <Select value={unitLabel} onValueChange={setUnitLabel}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="units">Units</SelectItem>
                <SelectItem value="kWh">kWh (Electricity)</SelectItem>
                <SelectItem value="liters">Liters</SelectItem>
                <SelectItem value="gallons">Gallons</SelectItem>
                <SelectItem value="cubic meters">Cubic Meters</SelectItem>
                <SelectItem value="hours">Hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Start From</Label>
            <div className="flex gap-2">
              <Select value={startMonth.toString()} onValueChange={v => setStartMonth(parseInt(v))}>
                <SelectTrigger className="flex-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={startYear.toString()} onValueChange={v => setStartYear(parseInt(v))}>
                <SelectTrigger className="w-24 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading || !name.trim()} className="bg-emerald-600 hover:bg-emerald-700">
            {loading && <Loader2 size={14} className="animate-spin mr-2" />}Next
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Category Management Modal ── */
function CategoryModal({ open, onClose, categories, onSave }) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setItems([...categories]); }, [open, categories]);

  const addCategory = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      const res = await api.post('/admin/categories', { name: newName.trim(), is_consumption_based: false });
      setItems([...items, res.data]);
      setNewName('');
      toast.success('Category added');
    } catch { toast.error('Failed to add category'); }
    finally { setLoading(false); }
  };

  const deleteCategory = async (id, isDefault) => {
    if (isDefault) {
      toast.error('Cannot delete default categories');
      return;
    }
    try {
      await api.delete(`/admin/categories/${id}`);
      setItems(items.filter(c => c.category_id !== id));
      toast.success('Category deleted');
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to delete'); 
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { onSave(items); onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Outfit']"><Settings size={18} className="text-slate-500" />Manage Categories</DialogTitle>
          <DialogDescription>Add or remove charge categories for your community</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-3">
          <div className="flex gap-2">
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New category name" className="flex-1" onKeyDown={e => e.key === 'Enter' && addCategory()} />
            <Button onClick={addCategory} disabled={loading || !newName.trim()} size="sm"><Plus size={14} /></Button>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {items.map(c => (
              <div key={c.category_id} className={`flex items-center justify-between p-2 rounded border ${c.is_default ? 'border-emerald-200 bg-emerald-50' : 'border-slate-100 hover:bg-slate-50'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm">{c.name}</span>
                  {c.is_default && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Default</Badge>}
                </div>
                {!c.is_default && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => deleteCategory(c.category_id, c.is_default)}><Trash2 size={14} /></Button>
                )}
              </div>
            ))}
            {items.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No categories yet</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main Page ── */
export default function GenerateDuesPage() {
  const [allUnits, setAllUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [items, setItems] = useState([{ description: '', amount: '', amount_type: 'total', category_id: '', unit_ids: [], consumption_service_id: '' }]);
  // (Opening Balance state removed)
  const [consumptionReadings, setConsumptionReadings] = useState({});
  const [communityName, setCommunityName] = useState('Your Community');
  
  // Month/Year selection
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  
  // Edit mode state
  const [editingPeriod, setEditingPeriod] = useState(null); // null or month_label being edited
  
  // State
  const [savingDraft, setSavingDraft] = useState(false);
  const [unitSummary, setUnitSummary] = useState([]);
  const [history, setHistory] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [activeTab, setActiveTab] = useState('consumption');
  
  // Consumption readings state
  const [readingsMonth, setReadingsMonth] = useState(new Date().getMonth() + 1);
  const [readingsYear, setReadingsYear] = useState(CURRENT_YEAR);
  const [savingReadings, setSavingReadings] = useState(false);
  const [savingServiceId, setSavingServiceId] = useState(null);
  const [serviceModal, setServiceModal] = useState({ open: false, editing: null });
  
  // Modals
  const [paymentModal, setPaymentModal] = useState({ open: false, unit: null, payments: [] });
  const [statementModal, setStatementModal] = useState({ open: false, unit: null });
  const [transactionsModal, setTransactionsModal] = useState({ open: false, unit: null });
  const [categoryModal, setCategoryModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [editPeriodModal, setEditPeriodModal] = useState({ open: false, month_label: '', payments: [] });
  const [editPaymentLoading, setEditPaymentLoading] = useState(false);
  
  // Statements tab state
  const [allStatements, setAllStatements] = useState({ units: [], totals: {} });
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [statementsFilter, setStatementsFilter] = useState('all'); // 'all', 'pending', 'clear'
  const [statementsSearch, setStatementsSearch] = useState('');

  // Detailed statement for payments tab
  const [detailedStatement, setDetailedStatement] = useState({ units: [], totals: {} });
  const [detailedStatementLoading, setDetailedStatementLoading] = useState(false);
  
  // Payment info for PDF export
  const [selectedPeriodDueDate, setSelectedPeriodDueDate] = useState('');
  
  // Period Settings (GPay/PhonePe/Comments)
  const [periodSettingsModal, setPeriodSettingsModal] = useState({ open: false, monthLabel: '' });
  const [periodSettings, setPeriodSettings] = useState({ gpay_id: '', phonepe_id: '', comments: '' });
  const [savingPeriodSettings, setSavingPeriodSettings] = useState(false);
  const [selectedPeriodSettings, setSelectedPeriodSettings] = useState({ gpay_id: '', phonepe_id: '', comments: '' });

  // Extra Payment Modal
  const [extraPaymentModal, setExtraPaymentModal] = useState({ open: false });
  const [extraPayment, setExtraPayment] = useState({ unit_id: '', amount: '', payment_method: 'upi', transaction_id: '', notes: '', description: 'Advance Payment', transaction_date: new Date().toISOString().slice(0, 10) });
  const [savingExtraPayment, setSavingExtraPayment] = useState(false);

  // Load initial data
  useEffect(() => { loadData(); }, []);

  // Update dates when month/year changes
  useEffect(() => {
    const y = selectedYear;
    const m = selectedMonth;
    const lastDay = new Date(y, m, 0).getDate();
    setFromDate(`${y}-${String(m).padStart(2, '0')}-01`);
    setToDate(`${y}-${String(m).padStart(2, '0')}-${lastDay}`);
    const nextMonth = m === 12 ? 1 : m + 1;
    const nextYear = m === 12 ? y + 1 : y;
    setDueDate(`${nextYear}-${String(nextMonth).padStart(2, '0')}-10`);
  }, [selectedMonth, selectedYear]);

  // Load consumption when readings month changes or services/units are loaded
  useEffect(() => { 
    if (services.length > 0 && allUnits.length > 0) {
      loadConsumptionReadings(); 
    }
  }, [readingsMonth, readingsYear, services.length, allUnits.length]);

  // Load statements when switching to statements tab
  useEffect(() => {
    if (activeTab === 'statements') {
      loadAllStatements();
    }
  }, [activeTab]);

  const loadData = async () => {
    try {
      const [unitsRes, histRes, catRes, svcRes, draftsRes, dashRes] = await Promise.all([
        api.get('/admin/maintenance/units'),
        api.get('/admin/maintenance/history'),
        api.get('/admin/categories'),
        api.get('/admin/consumption-services'),
        api.get('/admin/charge-drafts'),
        api.get('/admin/dashboard')
      ]);
      setAllUnits(unitsRes.data);
      setHistory(histRes.data);
      
      setCategories(catRes.data);
      setServices(svcRes.data);
      setDrafts(draftsRes.data);
      if (dashRes.data?.community_name) setCommunityName(dashRes.data.community_name);
    } catch (err) { console.error('Failed to load data', err); }
  };

  const loadConsumptionReadings = async () => {
    try {
      // First fetch the previous/opening readings (source of truth for previous)
      const prevRes = await api.get(`/admin/consumption-readings/previous?month=${readingsMonth}&year=${readingsYear}`);
      const prevData = prevRes.data || {};
      
      // Then fetch current month readings
      const res = await api.get(`/admin/consumption-readings?month=${readingsMonth}&year=${readingsYear}`);
      const readingsObj = {};
      
      // Build readings object - use prevData for "previous" field (not the stored previous_reading)
      res.data.forEach(r => {
        const key = `${r.service_id}|${r.unit_id}`;
        const prevValue = prevData[key] !== undefined ? prevData[key] : r.previous_reading;
        const consumed = Math.max(0, r.current_reading - prevValue);
        readingsObj[key] = { 
          previous: prevValue, 
          current: r.current_reading, 
          consumed: consumed 
        };
      });
      
      // For any unit/service combo without current month data, pre-fill previous from opening/previous month
      services.forEach(svc => {
        allUnits.forEach(u => {
          const key = `${svc.service_id}|${u.unit_id}`;
          if (!readingsObj[key] && prevData[key] !== undefined) {
            readingsObj[key] = { previous: prevData[key], current: '', consumed: 0 };
          }
        });
      });
      
      setConsumptionReadings(readingsObj);
    } catch { }
  };

  /* ── Load All Statements ── */
  const loadAllStatements = async () => {
    setStatementsLoading(true);
    try {
      const res = await api.get('/admin/all-unit-statements');
      setAllStatements(res.data);
    } catch { toast.error('Failed to load statements'); }
    finally { setStatementsLoading(false); }
  };

  /* ── Load Detailed Statement for Payments Tab ── */
  const loadDetailedStatement = async (period) => {
    setDetailedStatementLoading(true);
    try {
      const res = await api.get(`/admin/maintenance/detailed-statement?month_label=${encodeURIComponent(period)}`);
      setDetailedStatement(res.data);
    } catch { toast.error('Failed to load detailed statement'); }
    finally { setDetailedStatementLoading(false); }
  };

  /* ── Export to CSV ── */
  const exportToCSV = (data, filename, columns) => {
    const headers = columns.map(c => c.label).join(',');
    const rows = data.map(row => columns.map(c => {
      const val = row[c.key];
      // Handle values with commas or quotes
      if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val ?? '';
    }).join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Export downloaded');
  };

  /* ── Export Payments Statement ── */
  const exportPaymentsStatement = () => {
    // Calculate additional fields for CSV
    const dataWithCalculations = detailedStatement.units.map(u => {
      const thisMonth = (u.fixed_charges || 0) + (u.variable_charges || 0) + (u.other_charges || 0);
      const totalOutstanding = (u.previous_balance || 0) + thisMonth;
      const outstanding = totalOutstanding - (u.total_paid || 0);
      return {
        ...u,
        this_month: thisMonth,
        total_outstanding: totalOutstanding,
        outstanding: outstanding
      };
    });
    const columns = [
      { key: 'unit_number', label: 'Flat No' },
      { key: 'resident_name', label: 'Resident Name' },
      { key: 'previous_balance', label: 'Previous Balance' },
      { key: 'fixed_charges', label: 'Maintenance' },
      { key: 'variable_charges', label: 'Metered Charges' },
      { key: 'other_charges', label: 'Other' },
      { key: 'this_month', label: 'This Month' },
      { key: 'total_outstanding', label: 'Total Outstanding' },
      { key: 'total_paid', label: 'Paid' },
      { key: 'outstanding', label: 'Outstanding' }
    ];
    exportToCSV(dataWithCalculations, `Payments_${selectedPeriod}`, columns);
  };

  /* ── Export Unit Statements ── */
  const exportUnitStatements = () => {
    const columns = [
      { key: 'unit_number', label: 'Flat No' },
      { key: 'resident_name', label: 'Resident Name' },
      { key: 'resident_email', label: 'Email' },
      { key: 'resident_phone', label: 'Phone' },
      { key: 'total_charges', label: 'Total Charges' },
      { key: 'total_paid', label: 'Total Paid' },
      { key: 'balance_due', label: 'Balance Due' }
    ];
    exportToCSV(allStatements.units, 'Unit_Statements', columns);
  };

  /* ── Download Meter Readings Statement ── */
  const downloadMeterReadings = async (monthLabel) => {
    try {
      const res = await api.get(`/admin/maintenance/meter-readings-statement?month_label=${encodeURIComponent(monthLabel)}`);
      const data = res.data;
      
      if (!data.services || data.services.length === 0) {
        toast.info('No meter readings found for this period');
        return;
      }
      
      const doc = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const generatedDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      
      // Calculate grand total across all services
      let grandTotalUnits = 0;
      let grandTotalAmount = 0;
      data.services.forEach(service => {
        service.readings.forEach(r => {
          grandTotalUnits += r.units_consumed || 0;
          grandTotalAmount += r.amount || 0;
        });
      });
      
      // Header with FreeCoffee branding
      doc.setFillColor(6, 182, 212); // cyan-500
      doc.rect(0, 0, pageWidth, 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('freecoffee.in', pageWidth / 2, 7, { align: 'center' });
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('METER READINGS STATEMENT', pageWidth / 2, 15, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`${data.community_name || 'Community'} | ${monthLabel}`, pageWidth / 2, 22, { align: 'center' });
      doc.text(`Generated: ${generatedDate}`, pageWidth / 2, 27, { align: 'center' });
      
      // Grand Total Summary Box
      let yPos = 38;
      doc.setFillColor(236, 254, 255); // cyan-50
      doc.rect(10, yPos, pageWidth - 20, 12, 'F');
      doc.setDrawColor(6, 182, 212);
      doc.rect(10, yPos, pageWidth - 20, 12, 'S');
      doc.setTextColor(6, 182, 212);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`GRAND TOTAL: ${grandTotalUnits.toLocaleString()} units consumed | Rs. ${Math.round(grandTotalAmount).toLocaleString()}`, pageWidth / 2, yPos + 8, { align: 'center' });
      
      yPos = 58;
      
      // For each service
      data.services.forEach((service, svcIndex) => {
        if (service.readings.length === 0) return;
        
        // Check if we need a new page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        // Service header with total amount
        const serviceTotalUnits = service.readings.reduce((sum, r) => sum + (r.units_consumed || 0), 0);
        const serviceTotalAmount = Math.round(service.readings.reduce((sum, r) => sum + (r.amount || 0), 0));
        
        doc.setTextColor(6, 182, 212);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`${service.service_name} (Rate: Rs.${service.rate_per_unit}/${service.unit_label}) - Total: Rs. ${serviceTotalAmount.toLocaleString()}`, 10, yPos);
        yPos += 5;
        
        // Build table data - round all amounts, use Rs. instead of ₹
        const tableData = service.readings.map((r, idx) => [
          idx + 1,
          r.unit_number,
          r.resident_name || '-',
          r.previous_reading?.toLocaleString() || '0',
          r.current_reading?.toLocaleString() || '0',
          r.units_consumed?.toLocaleString() || '0',
          `Rs. ${Math.round(r.amount || 0).toLocaleString()}`
        ]);
        
        // Add totals row
        tableData.push(['', 'TOTAL', '', '', '', serviceTotalUnits.toLocaleString(), `Rs. ${serviceTotalAmount.toLocaleString()}`]);
        
        autoTable(doc, {
          startY: yPos,
          head: [['#', 'Flat', 'Resident', 'Previous', 'Current', 'Consumed', 'Amount']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [6, 182, 212], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { halign: 'center', cellWidth: 10 },
            1: { cellWidth: 20 },
            2: { cellWidth: 40 },
            3: { halign: 'right', cellWidth: 25 },
            4: { halign: 'right', cellWidth: 25 },
            5: { halign: 'right', cellWidth: 25 },
            6: { halign: 'right', cellWidth: 25 }
          },
          margin: { left: 10, right: 10 },
          didParseCell: (d) => {
            if (d.row.index === tableData.length - 1) {
              d.cell.styles.fillColor = [236, 254, 255]; // cyan-50
              d.cell.styles.fontStyle = 'bold';
            }
          }
        });
        
        yPos = doc.lastAutoTable.finalY + 15;
      });
      
      // Save
      doc.save(`Meter_Readings_${monthLabel.replace(' ', '_')}.pdf`);
      toast.success('Meter readings statement downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download meter readings');
    }
  };

  /* ── Export Payments to PDF ── */
  const exportPaymentsPDF = () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const generatedDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    
    // Header - Green bar
    doc.setFillColor(5, 150, 105);
    doc.rect(0, 0, pageWidth, 32, 'F');
    
    // Left side - Property Name, Period, Date
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(communityName, 14, 10);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Payment Statement - ${selectedPeriod}`, 14, 17);
    
    doc.setFontSize(9);
    // Format due date nicely
    const formattedDueDate = selectedPeriodDueDate ? new Date(selectedPeriodDueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
    doc.text(`Generated: ${generatedDate}${formattedDueDate ? ` | Due: ${formattedDueDate}` : ''}`, 14, 23);
    
    // Center - Payment Info (GPay/PhonePe)
    const centerX = pageWidth / 2;
    const settings = selectedPeriodSettings || {};
    if (settings.gpay_id || settings.phonepe_id) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      let paymentInfoY = 10;
      if (settings.gpay_id) {
        doc.text(`GPay: ${settings.gpay_id}`, centerX, paymentInfoY, { align: 'center' });
        paymentInfoY += 6;
      }
      if (settings.phonepe_id) {
        doc.text(`PhonePe: ${settings.phonepe_id}`, centerX, paymentInfoY, { align: 'center' });
        paymentInfoY += 6;
      }
    }
    
    // Comments in header
    if (settings.comments) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(settings.comments.substring(0, 80), centerX, 28, { align: 'center' });
    }
    
    // Right side - FreeCoffee branding
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('FreeCoffee.in', pageWidth - 14, 12, { align: 'right' });
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Your Free CommunityOS', pageWidth - 14, 19, { align: 'right' });
    
    // Table data - format numbers as whole rupees, show negative values with minus sign
    const formatNum = (n) => n !== 0 ? Math.round(n).toLocaleString('en-IN') : '-';
    const tableData = detailedStatement.units.map((u, i) => {
      const thisMonth = Math.round((u.fixed_charges || 0) + (u.variable_charges || 0) + (u.other_charges || 0));
      const totalOutstanding = Math.round((u.previous_balance || 0) + thisMonth);
      const outstanding = Math.round(totalOutstanding - (u.total_paid || 0));
      return [
        i + 1,
        u.unit_number,
        (u.resident_name || '-').substring(0, 15),
        formatNum(u.previous_balance),
        formatNum(u.fixed_charges),
        formatNum(u.variable_charges),
        formatNum(u.other_charges),
        thisMonth.toLocaleString('en-IN'),
        totalOutstanding.toLocaleString('en-IN'),
        formatNum(u.total_paid),
        outstanding.toLocaleString('en-IN')
      ];
    });
    
    // Add totals row
    const t = detailedStatement.totals;
    const totalThisMonth = (t.fixed_charges || 0) + (t.variable_charges || 0) + (t.other_charges || 0);
    const grandTotalOutstanding = (t.previous_balance || 0) + totalThisMonth;
    const finalOutstanding = grandTotalOutstanding - (t.total_paid || 0);
    tableData.push([
      '', 'TOTAL', '',
      (t.previous_balance || 0).toLocaleString('en-IN'),
      (t.fixed_charges || 0).toLocaleString('en-IN'),
      (t.variable_charges || 0).toLocaleString('en-IN'),
      (t.other_charges || 0).toLocaleString('en-IN'),
      totalThisMonth.toLocaleString('en-IN'),
      grandTotalOutstanding.toLocaleString('en-IN'),
      (t.total_paid || 0).toLocaleString('en-IN'),
      finalOutstanding.toLocaleString('en-IN')
    ]);
    
    // Calculate column widths to fill full A4 width (210mm - 14mm margins = 182mm usable)
    const usableWidth = pageWidth - 14; // 7mm margin on each side
    
    autoTable(doc, {
      startY: 40,
      head: [['#', 'Flat No', 'Resident Name', 'Prev Balance', 'Maintenance', 'Metered', 'Other', 'This Month', 'Total O/S', 'Paid', 'Outstanding']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 9, // Increased from 7 to 9 for better readability
        cellPadding: 3,
        lineColor: [226, 232, 240], // slate-200
        lineWidth: 0.3,
        overflow: 'linebreak'
      },
      headStyles: { 
        fillColor: [241, 245, 249], // slate-100 - matches UI header
        textColor: [51, 65, 85], // slate-700
        fontStyle: 'bold', 
        fontSize: 8,
        lineColor: [226, 232, 240],
        lineWidth: 0.3
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: usableWidth * 0.04 },   // # (4%)
        1: { cellWidth: usableWidth * 0.08 },                      // Flat No (8%)
        2: { cellWidth: usableWidth * 0.14 },                      // Resident Name (14%)
        3: { halign: 'right', cellWidth: usableWidth * 0.10, fontSize: 10 },    // Prev Balance (10%)
        4: { halign: 'right', cellWidth: usableWidth * 0.10, fontSize: 10 },    // Maintenance (10%)
        5: { halign: 'right', cellWidth: usableWidth * 0.10, fontSize: 10 },    // Metered (10%)
        6: { halign: 'right', cellWidth: usableWidth * 0.08, fontSize: 10 },    // Other (8%)
        7: { halign: 'right', cellWidth: usableWidth * 0.10, fontSize: 10 },    // This Month (10%)
        8: { halign: 'right', cellWidth: usableWidth * 0.10, fontSize: 10 },    // Total O/S (10%)
        9: { halign: 'right', cellWidth: usableWidth * 0.08, fontSize: 10 },    // Paid (8%)
        10: { halign: 'right', cellWidth: usableWidth * 0.08, fontSize: 11 }    // Outstanding (8%) - largest font
      },
      margin: { left: 7, right: 7 },
      tableWidth: usableWidth,
      didParseCell: (data) => {
        // TOTAL row styling
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fillColor = [241, 245, 249]; // slate-100
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 11;
        }
        
        // "This Month" column (index 7) - blue-50 background
        if (data.column.index === 7 && data.section === 'head') {
          data.cell.styles.fillColor = [239, 246, 255]; // blue-50
        }
        if (data.column.index === 7 && data.section === 'body') {
          data.cell.styles.fillColor = [239, 246, 255]; // blue-50
          data.cell.styles.fontStyle = 'bold';
        }
        
        // "Total O/S" column (index 8) - slate-100 background
        if (data.column.index === 8 && data.section === 'head') {
          data.cell.styles.fillColor = [241, 245, 249]; // slate-100
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.column.index === 8 && data.section === 'body') {
          data.cell.styles.fillColor = [241, 245, 249]; // slate-100
          data.cell.styles.fontStyle = 'bold';
        }
        
        // "Outstanding" column (index 10) - amber-50 background
        if (data.column.index === 10 && data.section === 'head') {
          data.cell.styles.fillColor = [255, 251, 235]; // amber-50
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.column.index === 10 && data.section === 'body' && data.row.index < tableData.length - 1) {
          data.cell.styles.fillColor = [255, 251, 235]; // amber-50
          data.cell.styles.fontStyle = 'bold';
        }
        
        // "Paid" column (index 9) - green text
        if (data.column.index === 9 && data.section === 'head') {
          data.cell.styles.textColor = [5, 150, 105]; // emerald-600
        }
        if (data.column.index === 9 && data.section === 'body') {
          data.cell.styles.textColor = [5, 150, 105]; // emerald-600
        }
        
        // Previous Balance column (index 3) - green if negative (credit)
        if (data.column.index === 3 && data.section === 'body' && data.row.index < tableData.length - 1) {
          const val = parseFloat(String(data.cell.raw).replace(/,/g, '') || 0);
          if (val < 0) {
            data.cell.styles.textColor = [5, 150, 105]; // emerald-600 for credit
          }
        }
        
        // Outstanding column (index 10) - text color based on value
        if (data.column.index === 10 && data.section === 'body' && data.row.index < tableData.length - 1) {
          const val = parseFloat(String(data.cell.raw).replace(/,/g, '') || 0);
          if (val > 0) {
            data.cell.styles.textColor = [180, 83, 9]; // amber-700 for due
          } else {
            data.cell.styles.textColor = [5, 150, 105]; // emerald-600 for credit/paid
          }
        }
      }
    });
    
    // Footer
    let finalY = doc.lastAutoTable.finalY + 8;
    
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('All amounts in INR | Generated via FreeCoffee.in', 10, finalY);
    
    doc.save(`Payment_Statement_${selectedPeriod}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF downloaded');
  };

  /* ── Export Unit Statements to PDF ── */
  const exportUnitStatementsPDF = () => {
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const generatedDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    
    // Header - Green bar
    doc.setFillColor(5, 150, 105);
    doc.rect(0, 0, pageWidth, 28, 'F');
    
    // Left side - Property Name, Report Type, Date
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(communityName, 14, 10);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Unit-wise Payment Statement', 14, 17);
    
    doc.setFontSize(9);
    doc.text(`Generated: ${generatedDate}`, 14, 23);
    
    // Right side - FreeCoffee branding
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('FreeCoffee.in', pageWidth - 14, 12, { align: 'right' });
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Your Free CommunityOS', pageWidth - 14, 19, { align: 'right' });
    
    // Summary
    const totals = allStatements.totals || {};
    doc.setFontSize(10);
    doc.setTextColor(30, 41, 59);
    doc.text(`Total Units: ${totals.total_units || 0}  |  Clear: ${totals.clear_units || 0}  |  With Balance: ${totals.pending_units || 0}`, 14, 38);
    
    // Table - numbers without ₹ symbol for cleaner display
    const tableData = allStatements.units.map((u, i) => [
      i + 1,
      u.unit_number,
      (u.resident_name || '-').substring(0, 20),
      u.total_charges.toLocaleString('en-IN'),
      u.total_paid.toLocaleString('en-IN'),
      u.balance_due.toLocaleString('en-IN')
    ]);
    
    // Add totals row
    tableData.push([
      '', 'TOTAL', '',
      (totals.total_charges || 0).toLocaleString('en-IN'),
      (totals.total_paid || 0).toLocaleString('en-IN'),
      (totals.total_balance || 0).toLocaleString('en-IN')
    ]);
    
    autoTable(doc, {
      startY: 44,
      head: [['#', 'Flat', 'Resident', 'Total Charges (Rs.)', 'Total Paid (Rs.)', 'Balance Due (Rs.)']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 9, 
        cellPadding: 3,
        lineColor: [200, 200, 200],
        lineWidth: 0.5
      },
      headStyles: { 
        fillColor: [5, 150, 105], 
        textColor: 255, 
        fontStyle: 'bold', 
        fontSize: 9,
        lineColor: [5, 150, 105],
        lineWidth: 0.5
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { cellWidth: 20 },
        2: { cellWidth: 45 },
        3: { halign: 'right', cellWidth: 35 },
        4: { halign: 'right', cellWidth: 35 },
        5: { halign: 'right', cellWidth: 35, fontStyle: 'bold' }
      },
      margin: { left: 14, right: 14 },
      didParseCell: (data) => {
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fillColor = [243, 244, 246];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.column.index === 5 && data.row.index < tableData.length - 1) {
          const val = parseFloat(String(data.cell.raw).replace(/,/g, '') || 0);
          if (val > 0) {
            data.cell.styles.textColor = [180, 83, 9];
          } else {
            data.cell.styles.textColor = [5, 150, 105];
          }
        }
      }
    });
    
    // Footer
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('Generated via FreeCoffee.in - Your Free CommunityOS', 14, finalY);
    
    doc.save(`Unit_Statements_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF downloaded');
  };

  /* ── Opening Balances ── */
  /* ── Consumption Readings ── */
  const handleSaveReadings = async () => {
    setSavingReadings(true);
    try {
      const readings = [];
      Object.entries(consumptionReadings).forEach(([key, val]) => {
        const [service_id, unit_id] = key.split('|');
        if (val.current !== undefined) {
          readings.push({ service_id, unit_id, month: readingsMonth, year: readingsYear, previous_reading: val.previous || 0, current_reading: val.current || 0 });
        }
      });
      await api.post('/admin/consumption-readings/bulk', { readings });
      toast.success('Readings saved');
      loadConsumptionReadings();
    } catch { toast.error('Failed to save readings'); }
    finally { setSavingReadings(false); }
  };

  const updateReading = (serviceId, unitId, field, value) => {
    const key = `${serviceId}|${unitId}`;
    setConsumptionReadings(prev => {
      const current = prev[key] || {};
      const newValue = value === '' ? '' : (parseFloat(value) || 0);
      const previousVal = current.previous || 0;
      
      return {
        ...prev,
        [key]: { 
          ...current, 
          current: newValue, 
          consumed: newValue === '' ? 0 : Math.max(0, newValue - previousVal),
          hasError: newValue !== '' && newValue < previousVal
        }
      };
    });
  };
  
  /* ── Validate reading on blur ── */
  const validateReading = (serviceId, unitId) => {
    const key = `${serviceId}|${unitId}`;
    const reading = consumptionReadings[key];
    if (reading && reading.current !== '' && reading.current < (reading.previous || 0)) {
      toast.error(`Current reading cannot be less than previous reading (${reading.previous || 0})`);
    }
  };

  /* ── Reset previous reading for a unit (edit opening balance) ── */
  const [resetReadingModal, setResetReadingModal] = useState({ open: false, serviceId: null, unitId: null, unitNumber: '', serviceName: '', currentPrevious: 0 });
  
  const handleResetReading = async () => {
    const { serviceId, unitId, newPrevious } = resetReadingModal;
    if (newPrevious === undefined || newPrevious === '') {
      toast.error('Enter a value');
      return;
    }
    try {
      await api.post('/admin/consumption-readings/reset-previous', {
        service_id: serviceId,
        unit_id: unitId,
        new_previous: parseFloat(newPrevious) || 0,
        month: readingsMonth,
        year: readingsYear
      });
      toast.success('Previous reading reset');
      setResetReadingModal({ open: false, serviceId: null, unitId: null, unitNumber: '', serviceName: '', currentPrevious: 0 });
      loadConsumptionReadings();
    } catch { toast.error('Failed to reset reading'); }
  };

  /* ── Save readings for a specific service ── */
  const handleSaveServiceReadings = async (serviceId) => {
    setSavingServiceId(serviceId);
    try {
      const readings = [];
      const errors = [];
      
      Object.entries(consumptionReadings).forEach(([key, val]) => {
        const [svc_id, unit_id] = key.split('|');
        if (svc_id === serviceId && val.current !== undefined && val.current !== '') {
          // Validate before adding
          if (val.current < (val.previous || 0)) {
            const unit = allUnits.find(u => u.unit_id === unit_id);
            errors.push(`${unit?.unit_number || unit_id}: Current (${val.current}) < Previous (${val.previous || 0})`);
          } else {
            readings.push({ 
              service_id: svc_id, 
              unit_id, 
              month: readingsMonth, 
              year: readingsYear, 
              previous_reading: val.previous || 0, 
              current_reading: val.current 
            });
          }
        }
      });
      
      if (readings.length === 0 && errors.length === 0) {
        toast.error('No readings to save for this service');
        setSavingServiceId(null);
        return;
      }
      
      const svc = services.find(s => s.service_id === serviceId);
      
      // Save valid readings even if some are invalid (partial save)
      if (readings.length > 0) {
        await api.post('/admin/consumption-readings/bulk', { readings });
      }
      
      if (errors.length > 0 && readings.length > 0) {
        toast.warning(`Saved ${readings.length} readings. Skipped invalid: ${errors.join(', ')}`);
      } else if (errors.length > 0) {
        toast.error(`Invalid readings (not saved): ${errors.join(', ')}`);
      } else {
        toast.success(`${svc?.name || 'Service'} readings saved (${readings.length})`);
      }
      
      loadConsumptionReadings();
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to save readings'); 
    }
    finally { setSavingServiceId(null); }
  };

  /* ── Clear readings for a specific service ── */
  const handleClearServiceReadings = async (serviceId, serviceName) => {
    if (!window.confirm(`Clear all ${serviceName} readings for ${MONTHS[readingsMonth - 1]} ${readingsYear}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/consumption-readings?service_id=${serviceId}&month=${readingsMonth}&year=${readingsYear}`);
      toast.success(`${serviceName} readings cleared`);
      // Clear from local state
      setConsumptionReadings(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(`${serviceId}|`)) {
            delete updated[key];
          }
        });
        return updated;
      });
      loadConsumptionReadings();
    } catch { toast.error('Failed to clear readings'); }
  };

  /* ── Clear a single unit's reading ── */
  const clearUnitReading = (serviceId, unitId) => {
    const key = `${serviceId}|${unitId}`;
    setConsumptionReadings(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  /* ── Delete a meter reading type ── */
  const handleDeleteService = async (serviceId) => {
    const svc = services.find(s => s.service_id === serviceId);
    if (!window.confirm(`Delete "${svc?.name}" meter reading type? This will also delete all readings for this type.`)) return;
    try {
      await api.delete(`/admin/consumption-services/${serviceId}`);
      toast.success(`${svc?.name} deleted`);
      loadData();
    } catch { toast.error('Failed to delete service'); }
  };

  /* ── Row CRUD ── */
  const addRow = () => setItems([...items, { description: '', amount: '', amount_type: 'total', category_id: '', unit_ids: [], consumption_service_id: '' }]);
  const removeRow = (i) => items.length > 1 && setItems(items.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [field]: val };
    if (field === 'amount_type' && val !== 'consumption') copy[i].consumption_service_id = '';
    setItems(copy);
  };

  const clearForm = () => {
    setItems([{ description: '', amount: '', amount_type: 'total', category_id: '', unit_ids: [], consumption_service_id: '' }]);
  };

  /* ── Load Draft to Form ── */
  const loadDraftToForm = (draft) => {
    // Set month/year from draft
    if (draft.month) setSelectedMonth(draft.month);
    if (draft.year) setSelectedYear(draft.year);
    
    // Set dates
    if (draft.from_date) setFromDate(draft.from_date);
    if (draft.to_date) setToDate(draft.to_date);
    if (draft.due_date) setDueDate(draft.due_date);
    
    // Load items
    if (draft.items && draft.items.length > 0) {
      setItems(draft.items.map(item => ({
        description: item.description || '',
        amount: item.amount?.toString() || '',
        amount_type: item.amount_type || 'total',
        category_id: item.category_id || '',
        unit_ids: item.unit_ids || [],
        consumption_service_id: item.consumption_service_id || ''
      })));
    }
    
    // Switch to Create Charges tab
    setActiveTab('create');
    toast.success(`Draft "${draft.month_label}" loaded. Edit and save when ready.`);
  };

  /* ── Delete Draft ── */
  const deleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/charge-drafts/${draftId}`);
      toast.success('Draft deleted');
      loadData();
    } catch { toast.error('Failed to delete draft'); }
  };

  /* ── Draft Operations ── */
  const handleSaveDraft = async (status = 'draft') => {
    const valid = items.filter(i => i.description && parseFloat(i.amount) > 0);
    if (valid.length === 0) { toast.error('Add at least one charge'); return; }
    
    // For 'saved' status, also validate dates
    if (status === 'saved' && (!fromDate || !toDate || !dueDate)) {
      toast.error('Fill in all dates for final save');
      return;
    }
    
    setSavingDraft(true);
    try {
      const monthLabel = `${MONTHS[selectedMonth]} ${selectedYear}`;
      
      // If in editing mode, delete existing payments first
      if (editingPeriod) {
        await api.put('/admin/maintenance/regenerate', { month_label: editingPeriod });
      }
      
      const draftRes = await api.post('/admin/charge-drafts', {
        month: selectedMonth,
        year: selectedYear,
        items: valid,
        from_date: fromDate,
        to_date: toDate,
        due_date: dueDate,
        status
      });
      
      // If saving as final (not draft), generate the actual payment records
      if (status === 'saved') {
        const payload = {
          draft_id: draftRes.data.draft_id,
          items: valid.map(i => ({
            description: i.description,
            amount: parseFloat(i.amount),
            amount_type: i.amount_type,
            category_id: i.category_id || null,
            unit_ids: (i.unit_ids?.length > 0) ? i.unit_ids : null,
            consumption_service_id: i.amount_type === 'consumption' ? i.consumption_service_id : null
          })),
          include_pending_balance: false, // Opening Balance feature removed
          from_date: fromDate,
          to_date: toDate,
          due_date: dueDate,
          month_label: editingPeriod || monthLabel,
          month: selectedMonth,
          year: selectedYear
        };
        
        const res = await api.post('/admin/maintenance/generate', payload);
        toast.success(editingPeriod ? `Re-generated ${editingPeriod}` : (res.data.message || 'Charges saved and generated'));
        
        // Clear editing mode
        setEditingPeriod(null);
        clearForm();
        loadData();
        
        // Load the new period in payments tab
        const summaryRes = await api.get(`/admin/maintenance/unit-summary?month_label=${encodeURIComponent(editingPeriod || monthLabel)}`);
        setUnitSummary(summaryRes.data);
        setSelectedPeriod(editingPeriod || monthLabel);
        setActiveTab('payments');
      } else {
        toast.success('Draft saved');
        loadData();
      }
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to save'); 
    }
    finally { setSavingDraft(false); }
  };

  /* ── Load period payments ── */
  const loadPeriod = async (ml) => {
    setLoadingPayments(true);
    try {
      const [summaryRes, settingsRes] = await Promise.all([
        api.get(`/admin/maintenance/unit-summary?month_label=${encodeURIComponent(ml)}`),
        api.get(`/admin/period-settings/${encodeURIComponent(ml)}`).catch(() => ({ data: { gpay_id: '', phonepe_id: '', comments: '' } }))
      ]);
      setUnitSummary(summaryRes.data);
      setSelectedPeriod(ml);
      setSelectedPeriodSettings(settingsRes.data || { gpay_id: '', phonepe_id: '', comments: '' });
      
      // Get due_date from history
      const periodInfo = history.find(h => h.month_label === ml);
      if (periodInfo && periodInfo.due_date) {
        setSelectedPeriodDueDate(periodInfo.due_date);
      } else {
        setSelectedPeriodDueDate('');
      }
      
      loadDetailedStatement(ml);
      setActiveTab('payments');
    } catch { toast.error('Failed to load'); }
    finally { setLoadingPayments(false); }
  };

  /* ── Load period settings ── */
  const loadPeriodSettings = async (monthLabel) => {
    try {
      const res = await api.get(`/admin/period-settings/${encodeURIComponent(monthLabel)}`);
      setPeriodSettings(res.data || { gpay_id: '', phonepe_id: '', comments: '' });
    } catch {
      setPeriodSettings({ gpay_id: '', phonepe_id: '', comments: '' });
    }
  };

  /* ── Save period settings ── */
  const handleSavePeriodSettings = async () => {
    setSavingPeriodSettings(true);
    try {
      await api.post('/admin/period-settings', {
        month_label: periodSettingsModal.monthLabel,
        gpay_id: periodSettings.gpay_id || '',
        phonepe_id: periodSettings.phonepe_id || '',
        comments: periodSettings.comments || ''
      });
      toast.success('Payment information saved');
      
      // Update selected period settings if this is the current period
      if (selectedPeriod === periodSettingsModal.monthLabel) {
        setSelectedPeriodSettings({ ...periodSettings });
      }
      
      setPeriodSettingsModal({ open: false, monthLabel: '' });
    } catch { toast.error('Failed to save settings'); }
    finally { setSavingPeriodSettings(false); }
  };

  /* ── Record Extra Payment (Advance/Credit) ── */
  const handleRecordExtraPayment = async () => {
    if (!extraPayment.unit_id || !extraPayment.amount || parseFloat(extraPayment.amount) <= 0) {
      toast.error('Please select a unit and enter a valid amount');
      return;
    }
    
    setSavingExtraPayment(true);
    try {
      const res = await api.post('/admin/extra-payment', {
        unit_id: extraPayment.unit_id,
        amount: parseFloat(extraPayment.amount),
        payment_method: extraPayment.payment_method,
        transaction_id: extraPayment.transaction_id || null,
        notes: extraPayment.notes || null,
        description: extraPayment.description || 'Advance Payment',
        transaction_date: extraPayment.transaction_date ? `${extraPayment.transaction_date}T00:00:00+00:00` : null
      });
      
      toast.success(res.data.message);
      setExtraPaymentModal({ open: false });
      setExtraPayment({ unit_id: '', amount: '', payment_method: 'upi', transaction_id: '', notes: '', description: 'Advance Payment', transaction_date: new Date().toISOString().slice(0, 10) });
      
      // Reload the current period data if viewing one
      if (selectedPeriod) {
        loadDetailedStatement(selectedPeriod);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record extra payment');
    } finally {
      setSavingExtraPayment(false);
    }
  };

  /* ── Download Outstanding Statement PDF ── */
  const downloadOutstandingStatement = async (monthLabel) => {
    try {
      const res = await api.get(`/admin/outstanding-statement/${encodeURIComponent(monthLabel)}`);
      const data = res.data;
      
      if (!data.units || data.units.length === 0) {
        toast.info('No outstanding amounts for this period. All units are paid!');
        return;
      }
      
      const doc = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const generatedDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const settings = data.settings || {};
      
      // Calculate header height based on payment info
      const hasPaymentInfo = settings.gpay_id || settings.phonepe_id;
      const headerHeight = hasPaymentInfo ? 42 : 35;
      
      // Header - FreeCoffee branded green bar
      doc.setFillColor(5, 150, 105); // emerald-600
      doc.rect(0, 0, pageWidth, headerHeight, 'F');
      
      // Left side - Community & Period info
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(data.community_name || 'Community', 12, 12);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Outstanding Statement - ${monthLabel}`, 12, 20);
      
      doc.setFontSize(9);
      doc.text(`Generated: ${generatedDate}`, 12, 27);
      
      // Payment Info in header (center section)
      if (hasPaymentInfo) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        const centerX = pageWidth / 2;
        let payY = 12;
        if (settings.gpay_id) {
          doc.text(`GPay: ${settings.gpay_id}`, centerX, payY, { align: 'center' });
          payY += 6;
        }
        if (settings.phonepe_id) {
          doc.text(`PhonePe: ${settings.phonepe_id}`, centerX, payY, { align: 'center' });
          payY += 6;
        }
        if (settings.comments) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.text(settings.comments.substring(0, 50), centerX, payY, { align: 'center' });
        }
      }
      
      // Right side - FreeCoffee branding
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('FreeCoffee', pageWidth - 12, 12, { align: 'right' });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Your Free CommunityOS', pageWidth - 12, 19, { align: 'right' });
      
      doc.setFontSize(8);
      doc.text('freecoffee.in', pageWidth - 12, 26, { align: 'right' });
      
      // Summary box
      let yPos = headerHeight + 8;
      doc.setFillColor(255, 251, 235); // amber-50
      doc.rect(10, yPos, pageWidth - 20, 12, 'F');
      doc.setDrawColor(217, 119, 6); // amber-600
      doc.rect(10, yPos, pageWidth - 20, 12, 'S');
      
      doc.setTextColor(146, 64, 14); // amber-800
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`${data.total_units_with_outstanding} Units with Outstanding | Total: Rs. ${data.total_outstanding.toLocaleString('en-IN')}`, pageWidth / 2, yPos + 8, { align: 'center' });
      
      yPos += 18;
      
      // Table data - Use "Rs." instead of ₹ (Unicode not supported in jsPDF default font)
      const tableData = data.units.map((u, idx) => [
        idx + 1,
        u.unit_number,
        u.resident_name || '-',
        `Rs. ${u.outstanding_amount.toLocaleString('en-IN')}`
      ]);
      
      // Add totals row
      tableData.push(['', 'TOTAL', '', `Rs. ${data.total_outstanding.toLocaleString('en-IN')}`]);
      
      // Use fixed column widths that fit A4 page (210mm - 20mm margins = 190mm)
      autoTable(doc, {
        startY: yPos,
        head: [['#', 'Unit No.', 'Resident Name', 'Outstanding Amount']],
        body: tableData,
        theme: 'grid',
        styles: { 
          fontSize: 10, 
          cellPadding: 4,
          lineColor: [226, 232, 240],
          lineWidth: 0.3,
          overflow: 'linebreak'
        },
        headStyles: { 
          fillColor: [5, 150, 105], 
          textColor: 255, 
          fontStyle: 'bold',
          fontSize: 10
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 12 },     // # - narrow
          1: { halign: 'center', cellWidth: 25 },     // Unit No
          2: { cellWidth: 90 },                        // Resident Name - wide
          3: { halign: 'right', cellWidth: 55, fontStyle: 'bold', fontSize: 11 }  // Amount - enough space
        },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto',
        didParseCell: (d) => {
          // Total row styling
          if (d.row.index === tableData.length - 1) {
            d.cell.styles.fillColor = [255, 251, 235]; // amber-50
            d.cell.styles.fontStyle = 'bold';
            d.cell.styles.fontSize = 11;
          }
          // Amount column - amber color for outstanding
          if (d.column.index === 3 && d.section === 'body') {
            d.cell.styles.textColor = [180, 83, 9]; // amber-700
          }
        }
      });
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('All amounts in INR | Generated via FreeCoffee.in', pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      
      doc.save(`Outstanding_Statement_${monthLabel.replace(' ', '_')}.pdf`);
      toast.success('Outstanding statement downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download outstanding statement');
    }
  };

  /* ── Load period template for editing ── */
  const loadPeriodForEdit = async (ml) => {
    try {
      const res = await api.get(`/admin/maintenance/template?month_label=${encodeURIComponent(ml)}`);
      const template = res.data;
      
      // Load template into Create Charges form
      setSelectedMonth(template.month || new Date().getMonth() + 1);
      setSelectedYear(template.year || CURRENT_YEAR);
      setFromDate(template.from_date || '');
      setToDate(template.to_date || '');
      setDueDate(template.due_date || '');
      
      // Load items
      if (template.items && template.items.length > 0) {
        setItems(template.items.map(item => ({
          description: item.description || '',
          amount: item.amount || '',
          amount_type: item.amount_type || 'total',
          category_id: item.category_id || '',
          unit_ids: item.unit_ids || [],
          distribution: item.distribution || 'equal',
          apply_to: item.apply_to || 'all',
          consumption_service_id: item.consumption_service_id || ''
        })));
      }
      
      // Set editing mode
      setEditingPeriod(ml);
      
      // Switch to Create Charges tab
      setActiveTab('charges');
      
      toast.success(`Loaded template for ${ml}. Make changes and click "Save & Re-Generate".`);
    } catch (err) {
      // If no template found, show individual payments for manual editing
      if (err.response?.status === 404) {
        toast.error('No template found for this period. This period was created before template saving was added.');
        // Fallback to individual payment editing
        try {
          const res = await api.get(`/admin/maintenance/period-payments?month_label=${encodeURIComponent(ml)}`);
          setEditPeriodModal({ open: true, month_label: ml, payments: res.data });
        } catch { toast.error('Failed to load payments'); }
      } else {
        toast.error('Failed to load template');
      }
    }
  };

  /* ── Cancel editing mode ── */
  const cancelEditing = () => {
    setEditingPeriod(null);
    // Reset form
    setItems([{ description: '', amount: '', amount_type: 'total', category_id: '', unit_ids: [], consumption_service_id: '' }]);
    setFromDate('');
    setToDate('');
    setDueDate('');
    toast.info('Edit cancelled');
  };

  /* ── Update a single payment ── */
  const handleUpdatePayment = async (paymentId, updates) => {
    setEditPaymentLoading(true);
    try {
      await api.put(`/admin/payments/${paymentId}`, updates);
      // Refresh the payments list
      const res = await api.get(`/admin/maintenance/period-payments?month_label=${encodeURIComponent(editPeriodModal.month_label)}`);
      setEditPeriodModal(prev => ({ ...prev, payments: res.data }));
      toast.success('Payment updated');
    } catch { toast.error('Failed to update payment'); }
    finally { setEditPaymentLoading(false); }
  };

  /* ── Delete a single payment from edit modal ── */
  const handleDeleteSinglePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await api.delete(`/admin/payments/${paymentId}`);
      setEditPeriodModal(prev => ({
        ...prev,
        payments: prev.payments.filter(p => p.payment_id !== paymentId)
      }));
      toast.success('Payment deleted');
      loadData(); // Refresh history
    } catch { toast.error('Failed to delete payment'); }
  };

  /* ── Record Payment ── */
  const handleRecordPayment = async (paymentDetails) => {
    if (!paymentModal.unit) return;
    setPaymentLoading(true);
    try {
      // Get pending/partial payments across ALL months for this unit (oldest first),
      // so payments from the statement page cover prior outstanding + current month.
      const paymentsRes = await api.get('/admin/maintenance/generated');
      const unitPayments = paymentsRes.data
        .filter(p => p.unit_id === paymentModal.unit.unit_id && (p.status === 'pending' || p.status === 'partial'))
        .sort((a, b) => {
          // Oldest first using due_date / from_date
          const ad = a.from_date || a.due_date || a.created_at || '';
          const bd = b.from_date || b.due_date || b.created_at || '';
          return ad.localeCompare(bd);
        });
      
      // Apply payment to each pending payment (oldest first) until amount is exhausted
      let remainingAmount = paymentDetails.amount;
      let appliedToInvoices = 0;
      for (const payment of unitPayments) {
        if (remainingAmount <= 0) break;
        const owed = payment.amount - (payment.paid_amount || 0);
        if (owed <= 0) continue;
        const paymentAmount = Math.min(remainingAmount, owed);
        if (paymentAmount > 0) {
          await api.post(`/admin/payments/${payment.payment_id}/partial`, {
            amount: paymentAmount,
            payment_method: paymentDetails.payment_method,
            transaction_id: paymentDetails.transaction_id,
            transaction_date: paymentDetails.transaction_date,
            notes: paymentDetails.notes
          });
          remainingAmount -= paymentAmount;
          appliedToInvoices += paymentAmount;
        }
      }
      
      // If there's still remaining (overpayment), record it as an extra/advance payment
      if (remainingAmount > 0.0001) {
        await api.post('/admin/extra-payment', {
          unit_id: paymentModal.unit.unit_id,
          amount: remainingAmount,
          payment_method: paymentDetails.payment_method,
          transaction_id: paymentDetails.transaction_id || null,
          transaction_date: paymentDetails.transaction_date ? `${paymentDetails.transaction_date}T00:00:00+00:00` : null,
          notes: paymentDetails.notes || null,
          description: 'Advance / Overpayment Credit'
        });
      }
      
      toast.success(`Payment of ₹${paymentDetails.amount} recorded${remainingAmount > 0 ? ` (₹${remainingAmount.toFixed(2)} credited as advance)` : ''}`);
      setPaymentModal({ open: false, unit: null, payments: [] });
      
      // Refresh all data including the detailed statement
      if (selectedPeriod) {
        const [summaryRes, histRes, statementRes] = await Promise.all([
          api.get(`/admin/maintenance/unit-summary?month_label=${encodeURIComponent(selectedPeriod)}`),
          api.get('/admin/maintenance/history'),
          api.get(`/admin/maintenance/detailed-statement?month_label=${encodeURIComponent(selectedPeriod)}`)
        ]);
        setUnitSummary(summaryRes.data);
        setHistory(histRes.data);
        setDetailedStatement(statementRes.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
    finally { setPaymentLoading(false); }
  };

  const inputTotal = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  return (
    <div data-testid="generate-dues-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Maintenance Management</h1>
        <p className="text-sm text-slate-500 mt-1">Manage meter readings, charges, and payments</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 mb-6 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="consumption" className="text-xs gap-1"><Droplets size={13} /> Meter Readings</TabsTrigger>
          <TabsTrigger value="create" className="text-xs gap-1"><FileSpreadsheet size={13} /> Create Charges</TabsTrigger>
          <TabsTrigger value="payments" className="text-xs gap-1"><CreditCard size={13} /> Payments</TabsTrigger>
          <TabsTrigger value="statements" className="text-xs gap-1"><Users size={13} /> Unit Statements</TabsTrigger>
          <TabsTrigger value="history" className="text-xs gap-1"><History size={13} /> History</TabsTrigger>
        </TabsList>

        {/* ═══════════ OPENING BALANCES TAB REMOVED ═══════════ */}

        {/* ═══════════ CONSUMPTION / METER READINGS TAB ═══════════ */}
        <TabsContent value="consumption">
          <Card className="border-slate-100 mb-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-['Outfit'] flex items-center gap-2"><Droplets size={18} className="text-blue-500" />Meter Reading Types</CardTitle>
                  <CardDescription className="text-xs">Define custom meter reading types for your community (e.g., Water, Electricity, Gas, DG Backup)</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => setServiceModal({ open: true, editing: null })}>
                  <Plus size={14} />Add Reading Type
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {services.length === 0 ? (
                <div className="text-center py-6">
                  <Droplets size={32} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500 mb-2">No meter reading types configured yet.</p>
                  <p className="text-xs text-slate-400">Click "Add Reading Type" to define custom types like Water, Electricity, Gas, etc.</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {services.map(svc => (
                    <div key={svc.service_id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                      <Zap size={14} className="text-amber-500" />
                      <span className="text-sm font-medium">{svc.name}</span>
                      <span className="text-xs text-slate-400">({svc.unit_label})</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDeleteService(svc.service_id)}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {services.length > 0 && (
            <Card className="border-slate-100">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-['Outfit'] flex items-center gap-2"><FileSpreadsheet size={18} className="text-blue-500" />Enter Readings</CardTitle>
                    <CardDescription className="text-xs">Enter meter readings for each type. Save each type individually.</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={readingsMonth.toString()} onValueChange={v => setReadingsMonth(parseInt(v))}>
                      <SelectTrigger className="w-[120px] h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{MONTHS.slice(1).map((m, i) => <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={readingsYear.toString()} onValueChange={v => setReadingsYear(parseInt(v))}>
                      <SelectTrigger className="w-[90px] h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {services.map(svc => (
                  <div key={svc.service_id} className="mb-6 border rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between border-b">
                      <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Zap size={14} className="text-amber-500" />
                        {svc.name} <span className="text-xs font-normal text-slate-400">({svc.unit_label})</span>
                      </h3>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleClearServiceReadings(svc.service_id, svc.name)} 
                          className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 size={12} className="mr-1" />
                          Clear
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => handleSaveServiceReadings(svc.service_id)} 
                          disabled={savingServiceId === svc.service_id}
                          className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                        >
                          {savingServiceId === svc.service_id && <Loader2 size={12} className="animate-spin mr-1" />}
                          Save {svc.name} Readings
                        </Button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-white">
                            <TableHead className="text-xs">Unit</TableHead>
                            <TableHead className="text-xs">Resident</TableHead>
                            <TableHead className="text-xs text-right">Previous Reading</TableHead>
                            <TableHead className="text-xs text-right">Current Reading</TableHead>
                            <TableHead className="text-xs text-right">Units Consumed</TableHead>
                            <TableHead className="text-xs w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {allUnits.map(u => {
                            const key = `${svc.service_id}|${u.unit_id}`;
                            const reading = consumptionReadings[key] || {};
                            const hasReading = reading.current !== undefined || reading.previous !== undefined;
                            return (
                              <TableRow key={u.unit_id}>
                                <TableCell className="text-sm font-medium">{u.unit_number}</TableCell>
                                <TableCell className="text-xs text-slate-500">{u.resident_name || '-'}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <div className="w-24 h-8 flex items-center justify-end px-3 bg-slate-100 rounded-md text-sm text-slate-600">
                                      {reading.previous || 0}
                                    </div>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                                      title="Reset previous reading"
                                      onClick={() => setResetReadingModal({ 
                                        open: true, 
                                        serviceId: svc.service_id, 
                                        unitId: u.unit_id, 
                                        unitNumber: u.unit_number,
                                        serviceName: svc.name,
                                        currentPrevious: reading.previous || 0,
                                        newPrevious: reading.previous || 0
                                      })}
                                    >
                                      <RotateCcw size={12} />
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Input 
                                    type="number" 
                                    min={reading.previous || 0}
                                    value={reading.current === '' ? '' : (reading.current || '')} 
                                    onChange={e => updateReading(svc.service_id, u.unit_id, 'current', e.target.value)} 
                                    onBlur={() => validateReading(svc.service_id, u.unit_id)}
                                    className={`w-28 h-8 text-sm text-right ${reading.hasError ? 'border-red-500 bg-red-50' : ''}`}
                                    placeholder="0" 
                                  />
                                </TableCell>
                                <TableCell className={`text-right text-sm font-medium ${reading.hasError ? 'text-red-600' : 'text-blue-600'}`}>
                                  {reading.hasError ? 'Invalid' : (reading.consumed || 0)}
                                </TableCell>
                                <TableCell>
                                  {hasReading && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                                      onClick={() => clearUnitReading(svc.service_id, u.unit_id)}
                                    >
                                      <X size={14} />
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══════════ CREATE CHARGES TAB ═══════════ */}
        <TabsContent value="create">
          {/* Editing Mode Banner */}
          {editingPeriod && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Edit size={20} className="text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Editing: {editingPeriod}</p>
                  <p className="text-xs text-blue-600">Make changes and click "Save & Re-Generate" to update</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={cancelEditing} className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-100">
                <X size={14} /> Cancel Edit
              </Button>
            </div>
          )}
          
          <Card className="border-slate-100 mb-5">
            <CardContent className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Month</Label>
                  <Select value={selectedMonth.toString()} onValueChange={v => setSelectedMonth(parseInt(v))} disabled={!!editingPeriod}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.slice(1).map((m, i) => <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Year</Label>
                  <Select value={selectedYear.toString()} onValueChange={v => setSelectedYear(parseInt(v))} disabled={!!editingPeriod}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">From Date</Label>
                  <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="text-sm h-9" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">To Date</Label>
                  <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="text-sm h-9" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Due Date</Label>
                  <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="text-sm h-9" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100 mb-5">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-['Outfit']">Charge Items</CardTitle>
                <CardDescription className="text-xs">Add charges. For consumption-based, select the meter service.</CardDescription>
              </div>
              <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => setCategoryModal(true)}><Settings size={12} />Manage Categories</Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs w-[40px]">#</TableHead>
                      <TableHead className="text-xs min-w-[160px]">Description</TableHead>
                      <TableHead className="text-xs min-w-[120px]">Category</TableHead>
                      <TableHead className="text-xs min-w-[120px]">Distribution</TableHead>
                      <TableHead className="text-xs min-w-[100px]">Amount (₹)</TableHead>
                      <TableHead className="text-xs min-w-[120px]">Apply To</TableHead>
                      <TableHead className="text-xs w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-center text-sm text-slate-500">{i + 1}</TableCell>
                        <TableCell><Input value={item.description} onChange={e => updateRow(i, 'description', e.target.value)} placeholder="e.g. Maintenance" className="text-sm h-9" /></TableCell>
                        <TableCell>
                          <Select value={item.category_id || 'none'} onValueChange={v => updateRow(i, 'category_id', v === 'none' ? '' : v)}>
                            <SelectTrigger className="text-xs h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- None --</SelectItem>
                              {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Select value={item.amount_type} onValueChange={v => updateRow(i, 'amount_type', v)}>
                              <SelectTrigger className="text-xs h-9 flex-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="per_unit">Per Unit</SelectItem>
                                <SelectItem value="total">Equal Split</SelectItem>
                                <SelectItem value="consumption">Consumption</SelectItem>
                              </SelectContent>
                            </Select>
                            {item.amount_type === 'consumption' && services.length > 0 && (
                              <Select value={item.consumption_service_id || ''} onValueChange={v => updateRow(i, 'consumption_service_id', v)}>
                                <SelectTrigger className="text-xs h-9 w-[80px]"><SelectValue placeholder="Svc" /></SelectTrigger>
                                <SelectContent>{services.map(s => <SelectItem key={s.service_id} value={s.service_id}>{s.name}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                          </div>
                        </TableCell>
                        <TableCell><Input type="number" value={item.amount} onChange={e => updateRow(i, 'amount', e.target.value)} placeholder="0" className="text-sm h-9" /></TableCell>
                        <TableCell><UnitMultiSelect allUnits={allUnits} selected={item.unit_ids || []} onChange={ids => updateRow(i, 'unit_ids', ids)} /></TableCell>
                        <TableCell><Button variant="ghost" size="sm" onClick={() => removeRow(i)} disabled={items.length <= 1} className="text-red-400 hover:text-red-600 h-8 w-8 p-0"><Trash2 size={14} /></Button></TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={7} className="py-2">
                        <Button variant="ghost" size="sm" onClick={addRow} className="gap-1 text-xs text-emerald-600 hover:text-emerald-700"><Plus size={14} /> Add Row</Button>
                      </TableCell>
                    </TableRow>
                    <TableRow className="bg-slate-50 border-t-2">
                      <TableCell colSpan={4} className="text-right text-xs font-semibold">Total</TableCell>
                      <TableCell className="text-sm font-bold flex items-center gap-0.5"><IndianRupee size={14} /> {inputTotal.toLocaleString()}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            {!editingPeriod && (
              <Button variant="outline" onClick={() => handleSaveDraft('draft')} disabled={savingDraft} className="gap-2">{savingDraft && <Loader2 size={14} className="animate-spin" />}<Save size={14} />Save as Draft</Button>
            )}
            <Button onClick={() => handleSaveDraft('saved')} disabled={savingDraft || items.every(i => !i.description || !i.amount)} className={`gap-2 px-6 ${editingPeriod ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              {savingDraft ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              {editingPeriod ? 'Save & Re-Generate' : 'Save & Generate'}
            </Button>
          </div>
        </TabsContent>

        {/* ═══════════ PAYMENTS TAB ═══════════ */}
        <TabsContent value="payments">
          {/* Period selector — choose month directly without going to History */}
          <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-slate-50 border border-slate-100">
            <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Select Month:</Label>
            <Select
              value={selectedPeriod || ''}
              onValueChange={(ml) => { if (ml) loadPeriod(ml); }}
            >
              <SelectTrigger className="h-9 w-[280px] bg-white" data-testid="payments-period-select">
                <SelectValue placeholder="Choose a generated month..." />
              </SelectTrigger>
              <SelectContent>
                {history.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-400">No periods generated yet</div>
                ) : (
                  history.map(h => (
                    <SelectItem key={h.month_label} value={h.month_label} data-testid={`payments-period-${h.month_label.replace(/\s+/g, '-')}`}>
                      {h.month_label} {h.locked ? '🔒' : ''}
                      <span className="ml-2 text-[10px] text-slate-400">₹{h.total_amount?.toLocaleString()}</span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {selectedPeriod && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={() => { setSelectedPeriod(null); setDetailedStatement({}); setUnitSummary([]); }} data-testid="payments-period-clear">
                Clear
              </Button>
            )}
          </div>
          {!selectedPeriod ? (
            <Card className="border-slate-100">
              <CardContent className="p-10 text-center">
                <History size={32} className="text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Choose a month from the dropdown above to view and manage payments.</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 font-['Outfit']">{selectedPeriod} - Payment Statement</h2>
                  <p className="text-xs text-slate-500">{detailedStatement.units?.length || 0} units</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-50 text-emerald-700">{unitSummary.filter(u => u.all_paid).length} / {unitSummary.length} Fully Paid</Badge>
                  <Button variant="outline" size="sm" className="h-9 gap-1 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => setExtraPaymentModal({ open: true })}>
                    <Plus size={14} />Extra Payment
                  </Button>
                  <Button variant="outline" size="sm" className="h-9 gap-1" onClick={() => loadDetailedStatement(selectedPeriod)} disabled={detailedStatementLoading}>
                    <RefreshCw size={14} className={detailedStatementLoading ? 'animate-spin' : ''} />Refresh
                  </Button>
                  <Button variant="outline" size="sm" className="h-9 gap-1" onClick={exportPaymentsStatement} disabled={!detailedStatement.units?.length}>
                    <Download size={14} />CSV
                  </Button>
                  <Button variant="outline" size="sm" className="h-9 gap-1 border-red-200 text-red-700 hover:bg-red-50" onClick={exportPaymentsPDF} disabled={!detailedStatement.units?.length}>
                    <FileDown size={14} />PDF
                  </Button>
                </div>
              </div>

              {loadingPayments || detailedStatementLoading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div> : (
                <Card className="border-slate-100">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs w-[40px]">#</TableHead>
                            <TableHead className="text-xs">Flat No</TableHead>
                            <TableHead className="text-xs">Resident Name</TableHead>
                            <TableHead className="text-xs text-right">Previous Balance</TableHead>
                            <TableHead className="text-xs text-right">Maintenance</TableHead>
                            <TableHead className="text-xs text-right">Metered Charges</TableHead>
                            <TableHead className="text-xs text-right">Other</TableHead>
                            <TableHead className="text-xs text-right bg-blue-50">This Month</TableHead>
                            <TableHead className="text-xs text-right bg-slate-100 font-bold">Total Outstanding</TableHead>
                            <TableHead className="text-xs text-right text-emerald-700">Paid</TableHead>
                            <TableHead className="text-xs text-right bg-amber-50 font-bold">Outstanding</TableHead>
                            <TableHead className="text-xs w-[140px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {detailedStatement.units?.map((u, i) => {
                            const thisMonth = (u.fixed_charges || 0) + (u.variable_charges || 0) + (u.other_charges || 0);
                            const totalOutstanding = (u.previous_balance || 0) + thisMonth;
                            const outstanding = totalOutstanding - (u.total_paid || 0);
                            return (
                            <TableRow key={u.unit_id} className={outstanding > 0 ? 'bg-amber-50/30' : outstanding < 0 ? 'bg-emerald-50/30' : ''}>
                              <TableCell className="text-center text-sm text-slate-500">{i + 1}</TableCell>
                              <TableCell className="text-sm font-medium">{u.unit_number}</TableCell>
                              <TableCell className="text-xs text-slate-600">{u.resident_name || '-'}</TableCell>
                              <TableCell className={`text-sm text-right ${u.previous_balance < 0 ? 'text-emerald-600' : ''}`}>
                                {u.previous_balance !== 0 ? `₹${u.previous_balance.toLocaleString()}` : '-'}
                              </TableCell>
                              <TableCell className="text-sm text-right">{u.fixed_charges > 0 ? `₹${u.fixed_charges.toLocaleString()}` : '-'}</TableCell>
                              <TableCell className="text-sm text-right text-blue-600">{u.variable_charges > 0 ? `₹${u.variable_charges.toLocaleString()}` : '-'}</TableCell>
                              <TableCell className="text-sm text-right">{u.other_charges > 0 ? `₹${u.other_charges.toLocaleString()}` : '-'}</TableCell>
                              <TableCell className="text-sm text-right font-medium bg-blue-50">₹{thisMonth.toLocaleString()}</TableCell>
                              <TableCell className="text-sm text-right font-bold bg-slate-100">₹{totalOutstanding.toLocaleString()}</TableCell>
                              <TableCell className="text-sm text-right text-emerald-600">
                                {u.total_paid > 0 ? (
                                  <button 
                                    className="hover:underline cursor-pointer hover:bg-emerald-50 px-2 py-0.5 rounded transition-colors" 
                                    onClick={() => setTransactionsModal({ open: true, unit: u })}
                                    title="Click to view/edit payments"
                                  >
                                    ₹{u.total_paid.toLocaleString()}
                                  </button>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-sm text-right font-bold bg-amber-50">
                                {outstanding > 0 ? <span className="text-amber-700">₹{outstanding.toLocaleString()}</span> : 
                                 outstanding < 0 ? <span className="text-emerald-600">₹{outstanding.toLocaleString()} (Credit)</span> : 
                                 <span className="text-emerald-600">₹0</span>}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {outstanding > 0 ? (
                                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => setPaymentModal({ open: true, unit: { ...u, pending_amount: outstanding }, payments: [] })}>
                                      <CreditCard size={12} />Pay
                                    </Button>
                                  ) : (
                                    <Badge className="bg-emerald-50 text-emerald-600 text-[10px]"><CheckCircle size={10} className="mr-0.5" />{outstanding < 0 ? 'Credit' : 'Paid'}</Badge>
                                  )}
                                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => setStatementModal({ open: true, unit: u })}>
                                    <FileText size={12} />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )})}
                        </TableBody>
                      </Table>
                    </div>
                    {/* Summary Footer */}
                    <div className="border-t p-4 bg-slate-50">
                      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-4 text-center">
                        <div><p className="text-[10px] text-slate-500 uppercase">Prev Balance</p><p className={`text-sm font-bold ${(detailedStatement.totals?.previous_balance || 0) < 0 ? 'text-emerald-600' : ''}`}>₹{detailedStatement.totals?.previous_balance?.toLocaleString() || 0}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">Maintenance</p><p className="text-sm font-bold">₹{detailedStatement.totals?.fixed_charges?.toLocaleString() || 0}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">Metered</p><p className="text-sm font-bold text-blue-600">₹{detailedStatement.totals?.variable_charges?.toLocaleString() || 0}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">Other</p><p className="text-sm font-bold">₹{detailedStatement.totals?.other_charges?.toLocaleString() || 0}</p></div>
                        <div className="bg-blue-100 rounded p-1"><p className="text-[10px] text-blue-700 uppercase">This Month</p><p className="text-sm font-bold text-blue-700">₹{detailedStatement.totals?.current_month_total?.toLocaleString() || 0}</p></div>
                        <div className="bg-slate-200 rounded p-1"><p className="text-[10px] text-slate-700 uppercase">Total Outstanding</p><p className="text-sm font-bold text-slate-700">₹{((detailedStatement.totals?.previous_balance || 0) + (detailedStatement.totals?.current_month_total || 0)).toLocaleString()}</p></div>
                        <div><p className="text-[10px] text-emerald-600 uppercase">Total Paid</p><p className="text-sm font-bold text-emerald-600">₹{detailedStatement.totals?.total_paid?.toLocaleString() || 0}</p></div>
                        <div className={`${(detailedStatement.totals?.grand_total || 0) < 0 ? 'bg-emerald-100' : 'bg-amber-100'} rounded p-1 col-span-2 md:col-span-1`}>
                          <p className={`text-[10px] ${(detailedStatement.totals?.grand_total || 0) < 0 ? 'text-emerald-700' : 'text-amber-700'} uppercase`}>{(detailedStatement.totals?.grand_total || 0) < 0 ? 'Credit Balance' : 'Outstanding'}</p>
                          <p className={`text-lg font-bold ${(detailedStatement.totals?.grand_total || 0) < 0 ? 'text-emerald-700' : 'text-amber-700'}`}>₹{detailedStatement.totals?.grand_total?.toLocaleString() || 0}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* ═══════════ UNIT STATEMENTS TAB ═══════════ */}
        <TabsContent value="statements">
          <Card className="border-slate-100">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                    <Users size={18} className="text-indigo-500" />Unit-wise Payment Statements
                  </CardTitle>
                  <CardDescription className="text-xs">Complete payment history and balance status for all units</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input 
                      placeholder="Search units..." 
                      value={statementsSearch}
                      onChange={e => setStatementsSearch(e.target.value)}
                      className="pl-9 h-9 w-48 text-sm"
                    />
                  </div>
                  <Select value={statementsFilter} onValueChange={setStatementsFilter}>
                    <SelectTrigger className="w-[130px] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Units</SelectItem>
                      <SelectItem value="pending">With Balance</SelectItem>
                      <SelectItem value="clear">Clear</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="h-9 gap-1" onClick={loadAllStatements}>
                    <RefreshCw size={12} className={statementsLoading ? 'animate-spin' : ''} />
                    Refresh
                  </Button>
                  <Button variant="outline" size="sm" className="h-9 gap-1" onClick={exportUnitStatements} disabled={!allStatements.units?.length}>
                    <Download size={14} />CSV
                  </Button>
                  <Button variant="outline" size="sm" className="h-9 gap-1 border-red-200 text-red-700 hover:bg-red-50" onClick={exportUnitStatementsPDF} disabled={!allStatements.units?.length}>
                    <FileDown size={14} />PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Total Units</p>
                  <p className="text-2xl font-bold text-slate-900">{allStatements.totals?.total_units || 0}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-blue-600 mb-1">Total Charges</p>
                  <p className="text-2xl font-bold text-blue-700">₹{(allStatements.totals?.total_charges || 0).toLocaleString()}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <p className="text-xs text-emerald-600 mb-1">Total Collected</p>
                  <p className="text-2xl font-bold text-emerald-700">₹{(allStatements.totals?.total_paid || 0).toLocaleString()}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-xs text-amber-600 mb-1">Total Outstanding</p>
                  <p className="text-2xl font-bold text-amber-700">₹{(allStatements.totals?.total_balance || 0).toLocaleString()}</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex items-center gap-4 mb-4">
                <Badge className="bg-emerald-50 text-emerald-700">{allStatements.totals?.clear_units || 0} Clear</Badge>
                <Badge className="bg-amber-50 text-amber-700">{allStatements.totals?.pending_units || 0} With Balance</Badge>
              </div>

              {statementsLoading ? (
                <div className="flex justify-center py-12"><Loader2 size={32} className="animate-spin text-slate-400" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs w-[50px]">#</TableHead>
                        <TableHead className="text-xs">Unit</TableHead>
                        <TableHead className="text-xs">Resident</TableHead>
                        <TableHead className="text-xs text-right">Total Charges</TableHead>
                        <TableHead className="text-xs text-right">Total Paid</TableHead>
                        <TableHead className="text-xs text-right">Balance Due</TableHead>
                        <TableHead className="text-xs text-center">Transactions</TableHead>
                        <TableHead className="text-xs">Last Activity</TableHead>
                        <TableHead className="text-xs w-[120px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allStatements.units
                        ?.filter(u => {
                          // Filter by status
                          if (statementsFilter === 'pending' && u.balance_due <= 0) return false;
                          if (statementsFilter === 'clear' && u.balance_due > 0) return false;
                          // Filter by search
                          if (statementsSearch) {
                            const search = statementsSearch.toLowerCase();
                            return u.unit_number?.toLowerCase().includes(search) || 
                                   u.resident_name?.toLowerCase().includes(search) ||
                                   u.resident_email?.toLowerCase().includes(search);
                          }
                          return true;
                        })
                        .map((u, i) => (
                          <TableRow key={u.unit_id} className={u.balance_due > 0 ? 'bg-amber-50/30' : ''}>
                            <TableCell className="text-center text-sm text-slate-500">{i + 1}</TableCell>
                            <TableCell>
                              <p className="text-sm font-medium">{u.unit_number}</p>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm">{u.resident_name || '-'}</p>
                              {u.resident_email && <p className="text-xs text-slate-400">{u.resident_email}</p>}
                            </TableCell>
                            <TableCell className="text-sm text-right font-medium">₹{u.total_charges.toLocaleString()}</TableCell>
                            <TableCell className="text-sm text-right text-emerald-600">₹{u.total_paid.toLocaleString()}</TableCell>
                            <TableCell className="text-sm text-right font-bold">
                              {u.balance_due > 0 ? (
                                <span className="text-amber-600">₹{u.balance_due.toLocaleString()}</span>
                              ) : (
                                <span className="text-emerald-600">₹0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Badge variant="outline" className="text-[10px]">{u.charge_count} charges</Badge>
                                <Badge variant="outline" className="text-[10px]">{u.transaction_count} payments</Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {u.last_payment_date ? (
                                <span>Paid: {u.last_payment_date.split('T')[0]}</span>
                              ) : u.last_charge_date ? (
                                <span>Charged: {u.last_charge_date.split('T')[0]}</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-8 text-xs gap-1"
                                  onClick={() => setStatementModal({ open: true, unit: u })}
                                >
                                  <FileText size={12} />View
                                </Button>
                                {u.balance_due > 0 && (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="h-8 text-xs gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                    onClick={() => setPaymentModal({ open: true, unit: { ...u, pending_amount: u.balance_due }, payments: [] })}
                                  >
                                    <CreditCard size={12} />Pay
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      {allStatements.units?.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                            No units found. Add units first to see statements.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══════════ HISTORY TAB ═══════════ */}
        <TabsContent value="history">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-['Outfit']">Approved Periods</CardTitle>
                <CardDescription className="text-xs">Click to view payments and record collections</CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No dues generated yet</p> : (
                  <div className="space-y-3">
                    {history.map((h, i) => (
                      <div key={i} className={`p-4 rounded-lg border transition-all hover:shadow-sm ${selectedPeriod === h.month_label ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100'} ${h.is_locked ? 'bg-slate-50' : ''}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold cursor-pointer hover:text-emerald-600" onClick={() => loadPeriod(h.month_label)}>{h.month_label}</h3>
                            {h.is_locked && (
                              <Badge className="text-[10px] bg-red-100 text-red-700 gap-1">
                                <Lock size={10} />Locked
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-base font-bold mr-2">₹{h.total_amount?.toLocaleString()}</span>
                            {/* Set GPay/PhonePe/Comments */}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0 text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                loadPeriodSettings(h.month_label);
                                setPeriodSettingsModal({ open: true, monthLabel: h.month_label });
                              }}
                              title="Set GPay/PhonePe/Comments"
                            >
                              <Settings size={14} />
                            </Button>
                            {/* Download Report (same as Payments tab) */}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Load period first to get all data needed for PDF
                                loadPeriod(h.month_label);
                              }}
                              title="Download Payment Report"
                            >
                              <FileDown size={14} />
                            </Button>
                            {/* Outstanding Statement */}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0 text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadOutstandingStatement(h.month_label);
                              }}
                              title="Download Outstanding Statement"
                            >
                              <Receipt size={14} />
                            </Button>
                            {/* Download Meter Readings */}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0 text-cyan-500 hover:text-cyan-700 hover:bg-cyan-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadMeterReadings(h.month_label);
                              }}
                              title="Download Meter Readings"
                            >
                              <Droplets size={14} />
                            </Button>
                            {/* Lock/Unlock Period */}
                            {!h.is_locked ? (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Lock period ${h.month_label}? Once locked, no edits or deletes will be allowed.`)) {
                                    api.post('/admin/maintenance/lock-period', { month_label: h.month_label })
                                      .then(() => {
                                        toast.success(`${h.month_label} has been locked`);
                                        setHistory(history.map(x => x.month_label === h.month_label ? { ...x, is_locked: true } : x));
                                      })
                                      .catch(err => toast.error(err.response?.data?.detail || 'Failed to lock'));
                                  }
                                }}
                                title="Lock this period"
                              >
                                <Lock size={14} />
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-slate-400 cursor-not-allowed"
                                disabled
                                title="Locked - Only Super Admin can unlock"
                              >
                                <Lock size={14} />
                              </Button>
                            )}
                            {/* Edit Button - disabled if locked */}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className={`h-7 w-7 p-0 ${h.is_locked ? 'text-slate-300 cursor-not-allowed' : 'text-blue-400 hover:text-blue-600 hover:bg-blue-50'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (h.is_locked) {
                                  toast.error('Period is locked. Contact Super Admin to unlock.');
                                  return;
                                }
                                loadPeriodForEdit(h.month_label);
                              }}
                              disabled={h.is_locked}
                              title={h.is_locked ? 'Locked - cannot edit' : 'Edit payments'}
                            >
                              <Edit size={14} />
                            </Button>
                            {/* Delete Button - disabled if locked */}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className={`h-7 w-7 p-0 ${h.is_locked ? 'text-slate-300 cursor-not-allowed' : 'text-red-400 hover:text-red-600 hover:bg-red-50'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (h.is_locked) {
                                  toast.error('Period is locked. Contact Super Admin to unlock.');
                                  return;
                                }
                                if (window.confirm(`Delete all ${h.count} payment records for ${h.month_label}? This cannot be undone.`)) {
                                  api.delete(`/admin/maintenance/period?month_label=${encodeURIComponent(h.month_label)}`)
                                    .then(() => {
                                      toast.success(`Deleted ${h.month_label} records`);
                                      setHistory(history.filter(x => x.month_label !== h.month_label));
                                      if (selectedPeriod === h.month_label) {
                                        setSelectedPeriod('');
                                        setDetailedStatement({ units: [], totals: {} });
                                      }
                                    })
                                    .catch(err => toast.error(err.response?.data?.detail || 'Failed to delete'));
                                }
                              }}
                              disabled={h.is_locked}
                              title={h.is_locked ? 'Locked - cannot delete' : 'Delete all payments'}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 cursor-pointer" onClick={() => loadPeriod(h.month_label)}>
                          <Badge variant="secondary" className="text-[10px]">{h.count} records</Badge>
                          <Badge className="text-[10px] bg-emerald-50 text-emerald-700">{h.paid_count} paid</Badge>
                          <Badge className="text-[10px] bg-amber-50 text-amber-700">{h.count - h.paid_count} pending</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-['Outfit']">Saved Drafts</CardTitle>
                <CardDescription className="text-xs">Click "Load" to edit a draft in Create Charges tab</CardDescription>
              </CardHeader>
              <CardContent>
                {drafts.filter(d => d.status !== 'approved').length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No drafts</p> : (
                  <div className="space-y-3">
                    {drafts.filter(d => d.status !== 'approved').map(d => (
                      <div key={d.draft_id} className="p-4 rounded-lg border border-slate-100 hover:border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-semibold">{d.month_label}</h3>
                          <Badge className={d.status === 'draft' ? 'bg-slate-100 text-slate-600' : 'bg-blue-50 text-blue-600'}>{d.status}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-3">{d.items?.length || 0} charge items</p>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs gap-1"
                            onClick={() => loadDraftToForm(d)}
                          >
                            <FileSpreadsheet size={12} />Load Draft
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 text-xs gap-1 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteDraft(d.draft_id)}
                          >
                            <Trash2 size={12} />Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <PaymentModal open={paymentModal.open} onClose={() => setPaymentModal({ open: false, unit: null, payments: [] })} unit={paymentModal.unit} payments={paymentModal.payments} onSave={handleRecordPayment} loading={paymentLoading} />
      <StatementModal open={statementModal.open} onClose={() => setStatementModal({ open: false, unit: null })} unit={statementModal.unit} />
      <TransactionsModal 
        open={transactionsModal.open} 
        onClose={() => setTransactionsModal({ open: false, unit: null })} 
        unit={transactionsModal.unit} 
        monthLabel={selectedPeriod}
        onUpdate={() => {
          if (selectedPeriod) {
            loadDetailedStatement(selectedPeriod);
          }
        }}
      />
      <CategoryModal open={categoryModal} onClose={() => setCategoryModal(false)} categories={categories} onSave={setCategories} />
      <ServiceModal open={serviceModal.open} onClose={() => setServiceModal({ open: false, editing: null })} onSave={loadData} allUnits={allUnits} />
      
      {/* Reset Reading Modal */}
      <Dialog open={resetReadingModal.open} onOpenChange={() => setResetReadingModal({ open: false, serviceId: null, unitId: null, unitNumber: '', serviceName: '', currentPrevious: 0 })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Outfit']">
              <RotateCcw size={18} className="text-blue-500" />
              Reset Previous Reading
            </DialogTitle>
            <DialogDescription>
              Reset the previous reading for {resetReadingModal.unitNumber} - {resetReadingModal.serviceName}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Current Previous Reading</Label>
              <div className="h-10 flex items-center px-3 bg-slate-100 rounded-md text-sm text-slate-600">
                {resetReadingModal.currentPrevious}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">New Previous Reading *</Label>
              <Input 
                type="number" 
                min="0"
                value={resetReadingModal.newPrevious || ''} 
                onChange={e => setResetReadingModal(prev => ({ ...prev, newPrevious: e.target.value }))} 
                placeholder="Enter new value" 
                className="h-10"
              />
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
              This will update the opening/initial reading for this unit. Use this to correct meter reading errors.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetReadingModal({ open: false, serviceId: null, unitId: null, unitNumber: '', serviceName: '', currentPrevious: 0 })}>Cancel</Button>
            <Button onClick={handleResetReading} className="bg-blue-600 hover:bg-blue-700">
              Reset Reading
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════ EDIT PERIOD PAYMENTS MODAL ═══════════ */}
      <Dialog open={editPeriodModal.open} onOpenChange={(open) => !open && setEditPeriodModal({ open: false, month_label: '', payments: [] })}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Outfit']">
              <Edit size={18} className="text-blue-500" />
              Edit Payments - {editPeriodModal.month_label}
            </DialogTitle>
            <DialogDescription>
              Edit individual payment records. Changes are saved immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {editPeriodModal.payments.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No payments found</p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Flat</TableHead>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs">Category</TableHead>
                      <TableHead className="text-xs text-right">Amount (₹)</TableHead>
                      <TableHead className="text-xs w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editPeriodModal.payments.map(p => (
                      <TableRow key={p.payment_id}>
                        <TableCell className="text-sm font-medium">{p.unit_number || allUnits.find(u => u.unit_id === p.unit_id)?.unit_number || '-'}</TableCell>
                        <TableCell>
                          <Input 
                            defaultValue={p.description} 
                            className="h-8 text-sm w-full"
                            onBlur={(e) => {
                              if (e.target.value !== p.description) {
                                handleUpdatePayment(p.payment_id, { description: e.target.value });
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <select 
                            defaultValue={p.category_id || ''} 
                            className="h-8 text-xs border rounded px-2 w-full"
                            onChange={(e) => handleUpdatePayment(p.payment_id, { category_id: e.target.value })}
                          >
                            <option value="">Select</option>
                            {categories.map(c => (
                              <option key={c.category_id} value={c.category_id}>{c.name}</option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            defaultValue={p.amount} 
                            className="h-8 text-sm w-24 text-right"
                            onBlur={(e) => {
                              const newAmount = parseFloat(e.target.value);
                              if (newAmount !== p.amount) {
                                handleUpdatePayment(p.payment_id, { amount: newAmount });
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                            onClick={() => handleDeleteSinglePayment(p.payment_id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <p className="text-xs text-slate-500">{editPeriodModal.payments.length} payment records</p>
              <Button variant="outline" onClick={() => {
                setEditPeriodModal({ open: false, month_label: '', payments: [] });
                loadData(); // Refresh data
              }}>
                Done
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════ PERIOD SETTINGS MODAL (GPay/PhonePe/Comments) ═══════════ */}
      <Dialog open={periodSettingsModal.open} onOpenChange={(open) => !open && setPeriodSettingsModal({ open: false, monthLabel: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Outfit']">
              <Settings size={18} className="text-purple-500" />
              Payment Settings - {periodSettingsModal.monthLabel}
            </DialogTitle>
            <DialogDescription>Set GPay/PhonePe IDs and comments for this month. These will appear in all payment reports.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">GPay UPI ID</Label>
              <Input 
                value={periodSettings.gpay_id || ''} 
                onChange={(e) => setPeriodSettings(prev => ({ ...prev, gpay_id: e.target.value }))} 
                placeholder="e.g., yourname@gpay" 
                className="h-10"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">PhonePe UPI ID</Label>
              <Input 
                value={periodSettings.phonepe_id || ''} 
                onChange={(e) => setPeriodSettings(prev => ({ ...prev, phonepe_id: e.target.value }))} 
                placeholder="e.g., yourname@ybl" 
                className="h-10"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Comments / Notes</Label>
              <Textarea 
                value={periodSettings.comments || ''} 
                onChange={(e) => setPeriodSettings(prev => ({ ...prev, comments: e.target.value }))} 
                placeholder="e.g., Pay by 10th to avoid late fee"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPeriodSettingsModal({ open: false, monthLabel: '' })}>Cancel</Button>
            <Button onClick={handleSavePeriodSettings} disabled={savingPeriodSettings} className="bg-purple-600 hover:bg-purple-700">
              {savingPeriodSettings && <Loader2 size={14} className="animate-spin mr-2" />}Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════ EXTRA PAYMENT MODAL ═══════════ */}
      <Dialog open={extraPaymentModal.open} onOpenChange={(open) => !open && setExtraPaymentModal({ open: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Outfit']">
              <Plus size={18} className="text-blue-500" />
              Record Extra Payment
            </DialogTitle>
            <DialogDescription>Record advance payments, security deposits, or any extra payment not tied to monthly maintenance.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Select Unit *</Label>
              <Select value={extraPayment.unit_id} onValueChange={(v) => setExtraPayment(prev => ({ ...prev, unit_id: v }))}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select unit..." />
                </SelectTrigger>
                <SelectContent>
                  {allUnits.map(u => (
                    <SelectItem key={u.unit_id} value={u.unit_id}>{u.unit_number} - {u.resident_name || 'Vacant'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Amount (₹) *</Label>
              <Input 
                type="number"
                value={extraPayment.amount} 
                onChange={(e) => setExtraPayment(prev => ({ ...prev, amount: e.target.value }))} 
                placeholder="Enter amount" 
                className="h-10"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Payment Method</Label>
              <Select value={extraPayment.payment_method} onValueChange={(v) => setExtraPayment(prev => ({ ...prev, payment_method: v }))}>
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Description</Label>
              <Select value={extraPayment.description} onValueChange={(v) => setExtraPayment(prev => ({ ...prev, description: v }))}>
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Advance Payment">Advance Payment</SelectItem>
                  <SelectItem value="Security Deposit">Security Deposit</SelectItem>
                  <SelectItem value="Special Contribution">Special Contribution</SelectItem>
                  <SelectItem value="Overpayment Adjustment">Overpayment Adjustment</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Transaction Date *</Label>
              <Input 
                type="date"
                value={extraPayment.transaction_date} 
                onChange={(e) => setExtraPayment(prev => ({ ...prev, transaction_date: e.target.value }))} 
                className="h-10"
                data-testid="extra-payment-date-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">Determines which month this payment applies to (for backdated entries).</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Transaction ID / Reference</Label>
              <Input 
                value={extraPayment.transaction_id} 
                onChange={(e) => setExtraPayment(prev => ({ ...prev, transaction_id: e.target.value }))} 
                placeholder="e.g., UPI reference number" 
                className="h-10"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Notes</Label>
              <Textarea 
                value={extraPayment.notes} 
                onChange={(e) => setExtraPayment(prev => ({ ...prev, notes: e.target.value }))} 
                placeholder="Any additional notes"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtraPaymentModal({ open: false })}>Cancel</Button>
            <Button onClick={handleRecordExtraPayment} disabled={savingExtraPayment} className="bg-blue-600 hover:bg-blue-700">
              {savingExtraPayment && <Loader2 size={14} className="animate-spin mr-2" />}Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
